import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import {
  adminPosOverview,
  adminRunPosMatcher,
  adminListMollieTerminals,
  adminStartPosPayment,
  adminResolveAmbiguousMatch,
} from "@/lib/admin-pos-payments.functions";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, CreditCard, RefreshCw } from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/admin/openstaand-afhalen",
)({
  head: () => ({
    meta: [
      { title: "Admin — Openstaand bij afhalen" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PosPaymentsPage,
});

const eur = (cents: number) =>
  new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(
    cents / 100,
  );

const TERMINAL_LS_KEY = "elvion.pos.terminalId";

function PosPaymentsPage() {
  const { isAdmin, loading } = useAuth();
  const overview = useServerFn(adminPosOverview);
  const runMatcher = useServerFn(adminRunPosMatcher);
  const listTerminals = useServerFn(adminListMollieTerminals);
  const startPos = useServerFn(adminStartPosPayment);
  const resolveAmbiguous = useServerFn(adminResolveAmbiguousMatch);
  const qc = useQueryClient();

  const [terminalId, setTerminalId] = useState<string>("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-pos-overview"],
    queryFn: () => overview({}),
    enabled: isAdmin,
    refetchInterval: 60_000,
  });

  const terminalsQ = useQuery({
    queryKey: ["admin-pos-terminals"],
    queryFn: () => listTerminals({}),
    enabled: isAdmin,
    staleTime: 5 * 60_000,
  });

  // herstel laatst gekozen terminal
  useEffect(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(TERMINAL_LS_KEY) : null;
    if (saved) setTerminalId(saved);
  }, []);
  useEffect(() => {
    const list = terminalsQ.data?.terminals ?? [];
    if (!terminalId && list.length === 1) {
      setTerminalId(list[0].id);
      localStorage.setItem(TERMINAL_LS_KEY, list[0].id);
    }
  }, [terminalsQ.data, terminalId]);

  const onTerminalChange = (v: string) => {
    setTerminalId(v);
    if (v) localStorage.setItem(TERMINAL_LS_KEY, v);
  };

  const matcherMut = useMutation({
    mutationFn: () => runMatcher({}),
    onSuccess: (s) => {
      toast.success(
        `Matcher klaar: ${s.matched} gekoppeld, ${s.unmatched} zonder match, ${s.ambiguous} dubbelzinnig`,
      );
      qc.invalidateQueries({ queryKey: ["admin-pos-overview"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Matcher mislukt"),
  });

  const startMut = useMutation({
    mutationFn: (orderId: string) => startPos({ data: { orderId, terminalId } }),
    onSuccess: (r) => {
      toast.success(
        `Pinbetaling gestart (${eur(r.amountCents)}). Bedrag verschijnt nu op de terminal.`,
      );
      qc.invalidateQueries({ queryKey: ["admin-pos-overview"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Pinbetaling kon niet starten"),
  });

  const resolveMut = useMutation({
    mutationFn: (vars: { matchId: string; orderId: string }) =>
      resolveAmbiguous({ data: vars }),
    onSuccess: (r) => {
      toast.success(`Pinbetaling toegewezen aan ${r.orderNumber}`);
      qc.invalidateQueries({ queryKey: ["admin-pos-overview"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Toewijzen mislukt"),
  });

  if (loading) return <div className="p-8">Laden…</div>;
  if (!isAdmin) return <div className="p-8">Geen toegang.</div>;

  const terminals = terminalsQ.data?.terminals ?? [];
  const ambiguousMatches = (data?.matches ?? []).filter(
    (m: any) => m.status === "ambiguous",
  );

  return (
    <div className="p-6 space-y-8 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Openstaand bij afhalen</h1>
          <p className="text-muted-foreground max-w-2xl">
            Klik op <strong>Pin starten</strong> bij een order om het exacte
            restbedrag direct naar de Mollie Terminal te sturen. Het ordernummer
            wordt als metadata meegestuurd, dus de match is automatisch — geen
            bedragvergelijking nodig. De achtergrond-matcher draait elke 5
            minuten als vangnet.
          </p>
        </div>
        <Button
          onClick={() => matcherMut.mutate()}
          disabled={matcherMut.isPending}
          variant="outline"
        >
          <RefreshCw className={`size-4 mr-2 ${matcherMut.isPending ? "animate-spin" : ""}`} />
          Matcher nu draaien
        </Button>
      </div>

      <div className="rounded-xl border bg-card p-4 flex items-center gap-3 flex-wrap">
        <label className="text-sm font-medium">Actieve terminal:</label>
        {terminalsQ.isLoading ? (
          <span className="text-sm text-muted-foreground">Terminals laden…</span>
        ) : terminals.length === 0 ? (
          <span className="text-sm text-amber-700">
            Geen Mollie Terminals gevonden — controleer of POS actief is op je Mollie-account.
          </span>
        ) : (
          <select
            value={terminalId}
            onChange={(e) => onTerminalChange(e.target.value)}
            className="border rounded-md px-3 py-1.5 text-sm bg-background"
          >
            <option value="">— kies een terminal —</option>
            {terminals.map((t: any) => (
              <option key={t.id} value={t.id}>
                {t.description || t.id} {t.status !== "active" ? `(${t.status})` : ""}
              </option>
            ))}
          </select>
        )}
      </div>

      {ambiguousMatches.length > 0 && (
        <section className="rounded-xl border-2 border-amber-300 bg-amber-50/60">
          <header className="p-5 border-b border-amber-200 flex items-start gap-3">
            <AlertTriangle className="size-5 text-amber-700 mt-0.5 shrink-0" />
            <div>
              <h2 className="font-semibold text-amber-900">
                Handmatig toewijzen ({ambiguousMatches.length})
              </h2>
              <p className="text-xs text-amber-800 mt-1">
                Deze pinbetalingen passen op meerdere openstaande orders.
                Kies hieronder aan welke order het bedrag moet worden geboekt.
              </p>
            </div>
          </header>
          <ul className="divide-y divide-amber-200">
            {ambiguousMatches.map((m: any) => {
              const candidates: any[] = m.candidates ?? [];
              const pendingFor = (orderId: string) =>
                resolveMut.isPending &&
                resolveMut.variables?.matchId === m.id &&
                resolveMut.variables?.orderId === orderId;
              return (
                <li key={m.id} className="p-5 space-y-3">
                  <div className="flex items-baseline justify-between gap-4 flex-wrap text-sm">
                    <div>
                      <span className="font-semibold">{eur(m.amount_cents)}</span>
                      <span className="text-muted-foreground ml-2">
                        op terminal{" "}
                        <span className="font-mono text-xs">{m.terminal_id ?? "—"}</span>
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(m.paid_at ?? m.created_at).toLocaleString("nl-NL")}
                      {" · "}
                      <span className="font-mono">{m.mollie_payment_id}</span>
                    </div>
                  </div>

                  {candidates.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      Geen kandidaat-orders meer met dit exacte restbedrag —
                      mogelijk is een van de oorspronkelijke orders intussen
                      betaald of geannuleerd.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-xs text-muted-foreground uppercase">
                          <tr>
                            <th className="text-left py-2 pr-3">Ordernr.</th>
                            <th className="text-left py-2 pr-3">Klant</th>
                            <th className="text-left py-2 pr-3">Aangemaakt</th>
                            <th className="text-right py-2 pr-3">Totaal</th>
                            <th className="text-right py-2 pr-3">Open</th>
                            <th className="text-right py-2">Actie</th>
                          </tr>
                        </thead>
                        <tbody>
                          {candidates.map((c) => (
                            <tr key={c.id} className="border-t border-amber-100">
                              <td className="py-2 pr-3 font-mono">{c.order_number}</td>
                              <td className="py-2 pr-3">
                                <div>{c.guest_name ?? "—"}</div>
                                <div className="text-xs text-muted-foreground">
                                  {c.guest_email ?? ""}
                                </div>
                              </td>
                              <td className="py-2 pr-3 text-xs text-muted-foreground">
                                {new Date(c.created_at).toLocaleDateString("nl-NL")}
                              </td>
                              <td className="py-2 pr-3 text-right">
                                {eur(c.total_cents)}
                              </td>
                              <td className="py-2 pr-3 text-right font-semibold text-amber-800">
                                {eur(c.remaining_cents)}
                              </td>
                              <td className="py-2 text-right">
                                <Button
                                  size="sm"
                                  disabled={resolveMut.isPending}
                                  onClick={() =>
                                    resolveMut.mutate({
                                      matchId: m.id,
                                      orderId: c.id,
                                    })
                                  }
                                >
                                  {pendingFor(c.id)
                                    ? "Toewijzen…"
                                    : "Aan deze order toewijzen"}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="rounded-xl border bg-card">

        <header className="p-5 border-b">
          <h2 className="font-semibold">
            Openstaand restbedrag ({data?.outstanding.length ?? 0})
          </h2>
        </header>
        {isLoading ? (
          <div className="p-8 text-sm text-muted-foreground">Laden…</div>
        ) : (data?.outstanding.length ?? 0) === 0 ? (
          <div className="p-8 text-sm text-muted-foreground">
            Geen openstaande restbedragen.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground uppercase">
              <tr>
                <th className="text-left px-5 py-3">Order</th>
                <th className="text-left px-5 py-3">Klant</th>
                <th className="text-left px-5 py-3">Methode</th>
                <th className="text-right px-5 py-3">Totaal</th>
                <th className="text-right px-5 py-3">Betaald</th>
                <th className="text-right px-5 py-3">Open</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-right px-5 py-3">Actie</th>
              </tr>
            </thead>
            <tbody>
              {data?.outstanding.map((o: any) => {
                const canStart = Boolean(terminalId) && terminals.length > 0;
                const pending = startMut.isPending && startMut.variables === o.id;
                return (
                  <tr key={o.id} className="border-t">
                    <td className="px-5 py-3 font-mono">{o.order_number}</td>
                    <td className="px-5 py-3">
                      <div>{o.guest_name ?? "—"}</div>
                      <div className="text-xs text-muted-foreground">{o.guest_email}</div>
                    </td>
                    <td className="px-5 py-3">
                      {o.fulfillment_method === "pickup" ? "Afhalen" : o.fulfillment_method}
                    </td>
                    <td className="px-5 py-3 text-right">{eur(o.total_cents)}</td>
                    <td className="px-5 py-3 text-right">{eur(o.amount_paid_cents ?? 0)}</td>
                    <td className="px-5 py-3 text-right font-semibold text-amber-700">
                      {eur(o.remaining_cents)}
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">
                        {o.payment_status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Button
                        size="sm"
                        disabled={!canStart || pending}
                        onClick={() => startMut.mutate(o.id)}
                        title={!canStart ? "Kies eerst een terminal" : undefined}
                      >
                        <CreditCard className="size-4 mr-1.5" />
                        {pending ? "Bezig…" : "Pin starten"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className="rounded-xl border bg-card">
        <header className="p-5 border-b">
          <h2 className="font-semibold">Laatste pinbetalingen (Mollie Terminal)</h2>
          <p className="text-xs text-muted-foreground mt-1">
            <strong>matched</strong> = automatisch gekoppeld (via metadata of
            bedrag), <strong>unmatched</strong> = geen order met dit restbedrag,{" "}
            <strong>ambiguous</strong> = meerdere kandidaten — handmatig controleren.
          </p>
        </header>
        {(data?.matches.length ?? 0) === 0 ? (
          <div className="p-8 text-sm text-muted-foreground">
            Nog geen pinbetalingen verwerkt.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground uppercase">
              <tr>
                <th className="text-left px-5 py-3">Wanneer</th>
                <th className="text-left px-5 py-3">Mollie ID</th>
                <th className="text-left px-5 py-3">Ordernr.</th>
                <th className="text-left px-5 py-3">Order ID</th>
                <th className="text-right px-5 py-3">Bedrag</th>
                <th className="text-left px-5 py-3">Terminal ID</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Opmerking</th>
              </tr>
            </thead>
            <tbody>
              {data?.matches.map((m: any) => (
                <tr key={m.id} className="border-t align-top">
                  <td className="px-5 py-3 whitespace-nowrap">
                    {new Date(m.paid_at ?? m.created_at).toLocaleString("nl-NL")}
                  </td>
                  <td className="px-5 py-3 font-mono text-xs">{m.mollie_payment_id}</td>
                  <td className="px-5 py-3 font-mono">{m.order_number ?? "—"}</td>
                  <td
                    className="px-5 py-3 font-mono text-xs text-muted-foreground"
                    title={m.order_id ?? ""}
                  >
                    {m.order_id ? `${m.order_id.slice(0, 8)}…` : "—"}
                  </td>
                  <td className="px-5 py-3 text-right">{eur(m.amount_cents)}</td>
                  <td className="px-5 py-3 font-mono text-xs">{m.terminal_id ?? "—"}</td>
                  <td className="px-5 py-3">
                    <MatchBadge status={m.status} />
                  </td>
                  <td className="px-5 py-3 text-xs text-muted-foreground">{m.note ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function MatchBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    matched: "bg-emerald-100 text-emerald-800",
    unmatched: "bg-muted text-muted-foreground",
    ambiguous: "bg-amber-100 text-amber-800",
    error: "bg-red-100 text-red-800",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${map[status] ?? "bg-muted"}`}>
      {status}
    </span>
  );
}
